import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export function SpotlightOverlay({ target, text, title, onClose, onNext, hasNext }) {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!target) return;
    const el = document.querySelector(target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setRect(null);
    }
  }, [target]);

  useEffect(() => {
    const timer = setTimeout(measure, 300);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(timer); window.removeEventListener('resize', measure); };
  }, [measure]);

  if (!target) return null;

  const padding = 8;
  const tooltipStyle = rect ? {
    position: 'fixed',
    top: Math.min(rect.top + rect.height + padding + 12, window.innerHeight - 200),
    left: Math.max(16, Math.min(rect.left, window.innerWidth - 380)),
    zIndex: 10002,
  } : { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10002 };

  return createPortal(
    <>
      {/* Dark overlay with cutout */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }} onClick={onClose}>
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              {rect && (
                <rect
                  x={rect.left - padding} y={rect.top - padding}
                  width={rect.width + padding * 2} height={rect.height + padding * 2}
                  rx="8" fill="black"
                />
              )}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.65)" mask="url(#spotlight-mask)" />
        </svg>
        {/* Highlight border */}
        {rect && (
          <div style={{
            position: 'fixed',
            top: rect.top - padding, left: rect.left - padding,
            width: rect.width + padding * 2, height: rect.height + padding * 2,
            border: '2px solid #00A6A0', borderRadius: 8,
            boxShadow: '0 0 0 4px rgba(0,166,160,0.2), 0 0 20px rgba(0,166,160,0.3)',
            zIndex: 10001, pointerEvents: 'none',
            animation: 'pulse-border 2s ease-in-out infinite',
          }} />
        )}
      </div>
      {/* Tooltip */}
      <div style={tooltipStyle} onClick={e => e.stopPropagation()}>
        <div className="bg-background border border-[#00A6A0]/30 rounded-xl shadow-2xl p-4 max-w-[360px]"
          style={{ backdropFilter: 'blur(12px)' }}>
          <div className="flex items-start justify-between mb-2">
            <p className="text-sm font-semibold text-[#00A6A0]">{title}</p>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-0.5">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">{text}</p>
          {!rect && (
            <p className="text-[10px] text-amber-400 mt-2">Element not found on this page. Navigate to the relevant section first.</p>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={onClose}>
              Close
            </Button>
            {hasNext && (
              <Button size="sm" className="text-xs bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={onNext}>
                Next <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse-border {
          0%, 100% { box-shadow: 0 0 0 4px rgba(0,166,160,0.2), 0 0 20px rgba(0,166,160,0.3); }
          50% { box-shadow: 0 0 0 6px rgba(0,166,160,0.35), 0 0 30px rgba(0,166,160,0.4); }
        }
      `}</style>
    </>,
    document.body
  );
}
